import { useLoad } from "@redotech/react-util/load";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageIcon from "@redotech/redo-icons/message-check-square.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import {
  SendEmailStep,
  SendSmsStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { useContext, useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { AutomationContext } from "../steps/context";
import { AnalyticsBlock } from "./analytics-block";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface SendMessageNodeData {
  isEmail: boolean;
  deleteElements: (id: string) => void;
  automationId: string;
  templateId: string | undefined;
  originTemplateId: string | undefined;
  step: PartialWithRequiredType<SendEmailStep | SendSmsStep>;
}

export function SendMessageNode(data: {
  id: string;
  data: SendMessageNodeData;
  selected?: boolean;
}) {
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);
  const automationContext = useContext(AutomationContext);

  const sendMessageData = useMemo(() => {
    return data.data;
  }, [data.data]);

  const existingTemplate = useMemo(() => {
    if (!automationContext) {
      return null;
    }

    // If there is an origin template id, look for a customized or origin template.
    if (sendMessageData.originTemplateId) {
      const customizedTemplate = automationContext.emailTemplates.find(
        (template) =>
          template.originTemplateId === sendMessageData.originTemplateId,
      );
      if (customizedTemplate) {
        return customizedTemplate;
      }
      const originTemplate = automationContext.originEmailTemplates.find(
        (template) =>
          template._id.toString() === sendMessageData.originTemplateId,
      );
      if (originTemplate) {
        return originTemplate;
      }
    }

    const templateId =
      sendMessageData.templateId || sendMessageData.originTemplateId;
    if (!templateId) {
      return null;
    }

    if (sendMessageData.isEmail) {
      const template = automationContext.emailTemplates.find(
        (template) => template._id.toString() === templateId,
      );
      return template ?? null;
    } else {
      const template = automationContext.smsTemplates.find(
        (template) => template._id.toString() === templateId,
      );

      return template ?? null;
    }
  }, [
    sendMessageData.templateId,
    sendMessageData.originTemplateId,
    sendMessageData.isEmail,
    automationContext,
  ]);

  const metricsLoad = useLoad(async () => {
    if (!marketingRpcClient) {
      return;
    }
    const metrics = await marketingRpcClient.getOutreachAnalytics({
      groupBy: {
        automation: sendMessageData.automationId,
        automationStepId: data.id,
      },
    });
    return metrics;
  }, [
    marketingRpcClient,
    sendMessageData.automationId,
    sendMessageData.templateId,
    sendMessageData.isEmail,
  ]);

  const metrics = metricsLoad.value;

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        disabled={sendMessageData.step.disabled}
        icon={
          <RedoFeaturedIcon
            color="success"
            Icon={sendMessageData.isEmail ? MailIcon : MessageIcon}
            size="xs"
          />
        }
        onDelete={() => sendMessageData.deleteElements(data.id)}
        selected={data.selected}
        step={sendMessageData.step}
        title={sendMessageData.isEmail ? "Send email" : "Send SMS"}
      >
        <Flex className={NodeCSS.fullWidth} dir="column" justify="flex-start">
          <Text fontSize="xxs">Template</Text>
          {existingTemplate ? (
            <Text className={NodeCSS.valueText}>
              {existingTemplate.name || "-"}
            </Text>
          ) : (
            <Text className={NodeCSS.placeholderText}>
              {(sendMessageData.templateId ||
                sendMessageData.originTemplateId) &&
              existingTemplate !== null
                ? "Loading..."
                : "Needs configuration"}
            </Text>
          )}
        </Flex>
      </BaseNode>
      <AnalyticsBlock
        clicks={metrics?.uniqueClicks ?? 0}
        opened={metrics?.uniqueOpens ?? 0}
        revenue={metrics?.upsellRevenue ?? 0}
        sent={metrics?.sends ?? 0}
        skippedDueToSkipConditions={metrics?.skippedDueToSkipConditions ?? 0}
        skippedDueToUnsubscribe={metrics?.skippedDueToUnsubscribe ?? 0}
      />
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
